import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MOCR_SECTIONS } from "../data/mocrQuestions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MocrCallMode({ record, onRecord, onExit }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => {
    const all = {};
    MOCR_SECTIONS.forEach((s) => { all[s.key] = { ...(record.sections?.[s.key]?.answers || {}) }; });
    return all;
  });
  const [actions, setActions] = useState({});
  const [saving, setSaving] = useState(false);
  const [logged, setLogged] = useState([]);
  const loggedRef = useRef(new Set());

  const done = idx >= MOCR_SECTIONS.length;
  const meta = done ? null : MOCR_SECTIONS[idx];

  const setAns = (key, qid, v) => setAnswers((p) => ({ ...p, [key]: { ...p[key], [qid]: { explanation: "", ...p[key][qid], answer: v } } }));
  const setAct = (qid, field, v) => setActions((p) => ({ ...p, [qid]: { desc: "", owner: "", date: "", open: true, ...p[qid], [field]: v } }));
  const toggleAct = (qid) => setActions((p) => ({ ...p, [qid]: { desc: "", owner: "", date: "", ...p[qid], open: !p[qid]?.open } }));

  const actionComplete = (qid) => {
    const a = actions[qid];
    return a && a.open && a.desc.trim() && a.owner.trim() && a.date;
  };
  const satisfied = (key, q) => (answers[key][q.id]?.answer || "").trim() !== "" || actionComplete(q.id);
  const allSatisfied = meta ? meta.questions.every((q) => satisfied(meta.key, q)) : true;

  const advance = async () => {
    if (!meta || !allSatisfied) return;
    setSaving(true);
    try {
      const merged = { ...answers[meta.key] };
      const newCaps = [];
      for (const q of meta.questions) {
        if (actionComplete(q.id) && !loggedRef.current.has(q.id)) {
          const a = actions[q.id];
          const res = await axios.post(`${API}/caps`, {
            finding: `MOCR ${record.month} ${q.id} — open item from the MOCR call: ${a.desc}`,
            source: "MOCR exception",
            owner: a.owner,
            deadline: a.date,
            corrective_steps: a.desc,
            control: "Logged live during the MOCR call (call mode) — tracked to closure in the CAP register.",
          });
          loggedRef.current.add(q.id);
          newCaps.push(`${res.data.cap_number} — ${q.id} → ${a.owner} by ${a.date}`);
          merged[q.id] = {
            ...merged[q.id],
            answer: merged[q.id]?.answer?.trim() ? merged[q.id].answer : `ACTION ITEM → ${res.data.cap_number}`,
            explanation: `${merged[q.id]?.explanation ? merged[q.id].explanation + " · " : ""}Action item (${res.data.cap_number}): ${a.desc} — owner ${a.owner}, due ${a.date}`,
          };
        }
      }
      const res = await axios.put(`${API}/mocr/${record.month}/sections/${meta.key}`, { answers: merged });
      onRecord(res.data);
      setAnswers((p) => ({ ...p, [meta.key]: merged }));
      if (newCaps.length) {
        setLogged((p) => [...p, ...newCaps]);
        toast.success(`${newCaps.length} action item${newCaps.length !== 1 ? "s" : ""} logged to the CAP register`);
      }
      setIdx(idx + 1);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save this section");
    }
    setSaving(false);
  };

  return (
    <div className="callmode no-print" data-testid="mocr-call-mode">
      <div className="cm-top">
        <div className="cm-brand">MOCR {record.month} — CALL MODE</div>
        <div className="cm-progress">
          {MOCR_SECTIONS.map((s, i) => (
            <span key={s.key} className={`cm-step ${i < idx ? "done" : i === idx && !done ? "current" : ""}`}>{s.key}</span>
          ))}
          <span className={`cm-step ${done ? "current" : ""}`}>✓</span>
        </div>
        <button className="btn ghost cm-exit" onClick={onExit} data-testid="call-mode-exit">Exit call mode</button>
      </div>

      {!done ? (
        <div className="cm-body">
          <div className="cm-section-title">{meta.title}</div>
          <div className="cm-lane">{meta.lane} · every question needs an answer OR a named action item (owner + date) before this screen advances</div>
          {meta.questions.map((q) => {
            const ok = satisfied(meta.key, q);
            const act = actions[q.id];
            return (
              <div key={q.id} className={`cm-q ${ok ? "ok" : ""}`} data-testid={`callmode-q-${q.id}`}>
                <div className="cm-q-head"><span className="cm-qid">{q.id}</span><span className={`cm-check ${ok ? "on" : ""}`}>{ok ? "✓" : "•"}</span></div>
                <div className="cm-q-text">{q.text}</div>
                <input className="cm-input" placeholder="Answer live on the call — Yes / No / count / detail…"
                  value={answers[meta.key][q.id]?.answer || ""}
                  onChange={(e) => setAns(meta.key, q.id, e.target.value)}
                  data-testid={`callmode-answer-${q.id}`} />
                <button className="cm-act-toggle" onClick={() => toggleAct(q.id)} data-testid={`callmode-action-toggle-${q.id}`}>
                  {act?.open ? "− remove action item" : "＋ can't answer now → log an action item (auto-CAP)"}
                </button>
                {act?.open && (
                  <div className="cm-act">
                    <input className="cm-input sm" placeholder="What must happen" value={act.desc} onChange={(e) => setAct(q.id, "desc", e.target.value)} data-testid={`callmode-action-desc-${q.id}`} />
                    <input className="cm-input sm" style={{ maxWidth: 200 }} placeholder="Owner (person)" value={act.owner} onChange={(e) => setAct(q.id, "owner", e.target.value)} data-testid={`callmode-action-owner-${q.id}`} />
                    <input className="cm-input sm" style={{ maxWidth: 170 }} type="date" value={act.date} onChange={(e) => setAct(q.id, "date", e.target.value)} data-testid={`callmode-action-date-${q.id}`} />
                  </div>
                )}
              </div>
            );
          })}
          <div className="cm-foot">
            {idx > 0 && <button className="btn ghost" onClick={() => setIdx(idx - 1)} data-testid="callmode-back">← Back</button>}
            <button className="btn cm-next" onClick={advance} disabled={!allSatisfied || saving} data-testid="callmode-next">
              {saving ? "Saving…" : allSatisfied ? `Save & continue → ${idx + 1 < MOCR_SECTIONS.length ? "Section " + MOCR_SECTIONS[idx + 1].key : "wrap-up"}` : `${meta.questions.filter((q) => !satisfied(meta.key, q)).length} question(s) still need an answer or action item`}
            </button>
          </div>
        </div>
      ) : (
        <div className="cm-body" data-testid="callmode-done">
          <div className="cm-section-title">Call complete — {record.month}</div>
          <div className="cm-lane">All sections answered live. Action items are already on the CAP register.</div>
          {logged.length > 0 && (
            <div className="cm-q ok">
              <div className="cm-q-text"><b>Action items logged this call:</b></div>
              {logged.map((l, i) => <div key={i} className="cm-q-text" style={{ fontSize: 20 }}>· {l}</div>)}
            </div>
          )}
          <div className="cm-q">
            <div className="cm-q-text"><b>Before day 10:</b> each lane signs its attestation (Section A / B / C) in the form below, then the record is closed — "MOCR signed &amp; filed". Attestation and close timestamps are server-set at the moment of signing; if this happens after the 10th the record will honestly show the late date.</div>
          </div>
          <div className="cm-foot">
            <button className="btn cm-next" onClick={onExit} data-testid="callmode-finish">Exit to the form → attest &amp; file</button>
          </div>
        </div>
      )}
    </div>
  );
}
